import apiService from "./api";

class WebPlayerHeartbeatService {
  constructor() {
    this.intervalId = null;
    this.isRunning = false;
    this.listeners = new Set();
    this.lastData = null;
  }

  addListener(callback) {
    this.listeners.add(callback);
  }

  removeListener(callback) {
    this.listeners.delete(callback);
  }

  notifyListeners(data) {
    this.listeners.forEach((callback) => {
      try {
        callback(data);
      } catch (err) {
        console.error("Heartbeat Listener Error:", err);
      }
    });
  }

  async sendHeartbeat() {
    try {
      const heartbeat = await apiService.webPlayerHeartbeat();

      // Mirror server's recently-watched progress into localStorage on every
      // tick so any page using watchProgressUtils.enrichItemsWithProgress sees
      // fresh data without having to subscribe to the heartbeat directly.
      this.mergeRecentlyWatchedProgress(
        heartbeat?.data?.recentlyWatchedProgress
      );

      const data = {
        heartbeat: heartbeat || null,
        timestamp: Date.now(),
      };

      this.lastData = data;
      this.notifyListeners(data);
    } catch (err) {
      console.error("Heartbeat Error:", err);

      // Notify listeners about the error so HeartbeatManager can handle it
      const errorData = {
        heartbeat: {
          error: err.message || "Heartbeat failed",
          status: err.response?.status || 500,
          auth: false,
        },
        timestamp: Date.now(),
      };

      this.lastData = errorData;
      this.notifyListeners(errorData);
    }
  }

  start() {
    if (this.isRunning) {
      return;
    }

    const playlistId = localStorage.getItem("currentPlaylistId");

    if (!playlistId) {
      return;
    }

    this.isRunning = true;
    this.sendHeartbeat();

    this.intervalId = setInterval(() => {
      this.sendHeartbeat();
    }, 5000);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
    this.listeners.clear();
  }

  restart() {
    this.stop();
    this.start();
  }

  getLastData() {
    return this.lastData;
  }

  // Merge server entries into localStorage without clobbering fresher local
  // saves (the in-flight player save can land seconds before the server's
  // eventual-consistency catches up). Higher progress wins.
  mergeRecentlyWatchedProgress(progressData) {
    if (!Array.isArray(progressData) || progressData.length === 0) return;

    const storageKey = "webPlayer_recentlyWatchedProgress";
    let existing = [];

    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || "[]");
    } catch {
      existing = [];
    }

    const keyOf = (item) =>
      `${String(item.mediaId)}::${item.mediaType || "movie"}`;
    const merged = new Map();

    existing.forEach((item) => {
      if (item && item.mediaId != null) merged.set(keyOf(item), item);
    });

    progressData.forEach((item) => {
      if (item == null || item.mediaId == null) return;
      const serverEntry = {
        mediaId: item.mediaId,
        mediaType: item.mediaType || "movie",
        tmdbId: item.tmdbId,
        progress: item.progress || 0,
        duration: item.duration || 0,
        progressPercent: item.progressPercent || 0,
        watched: item.watched || false,
        isValid: (item.progress || 0) > 10000,
        title: item.title ?? null,
        posterUrl: item.posterUrl ?? null,
        thumbnailUrl: item.thumbnailUrl ?? null,
        streamUrl: item.streamUrl ?? null,
        episodeId: item.episodeId ?? null,
        episodeInfo: item.episodeInfo ?? null,
      };
      const k = keyOf(serverEntry);
      const local = merged.get(k);

      if (!local || (serverEntry.progress || 0) >= (local.progress || 0)) {
        merged.set(k, serverEntry);
      }
    });

    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify(Array.from(merged.values()))
      );
    } catch (e) {
      console.warn("Heartbeat: failed to write progress cache:", e);
    }
  }
}

const webPlayerHeartbeatService = new WebPlayerHeartbeatService();

export default webPlayerHeartbeatService;
